/**
 * @module engine/geometry
 * @summary Pure geometry helpers — ports, resize handles, distances.
 * @description Pure functions (no engine state). Used by rendering, hit-testing
 *   and connection routing.
 * @ported-from JCC PFD Engineering Tool v1.1.html lines 3140-3147, 3416-3417.
 */
import type { Shape, Handle, Point, PortKey } from '../types';
import { PORT_ANCHORS, type PortAnchor } from './shapes/ports';

/** Cardinal ports in preference order — first match wins ties in `nearPort`. */
const PORT_ORDER: PortKey[] = ['N', 'E', 'S', 'W'];

/**
 * The four connection ports of a shape as full anchors (point + outward normal).
 *
 * Two things happen here that raw bbox midpoints can't express:
 *   1. Shape types in `PORT_ANCHORS` (elbows) override the faces that the bbox
 *      midpoints get wrong.
 *   2. Rotation is applied. The renderer rotates a shape's art about its bbox
 *      centre, so its ports have to travel with it — otherwise a rotated
 *      symbol's pipe mouths swing away while its connectors stay behind.
 *      // INVARIANT: this mirrors the transform in render.ts and the exporters.
 */
export function getPortAnchors(s: Shape): Record<PortKey, PortAnchor> {
  const base: Record<PortKey, PortAnchor> = {
    N: { p: { x: s.x + s.w / 2, y: s.y }, n: { x: 0, y: -1 } },
    S: { p: { x: s.x + s.w / 2, y: s.y + s.h }, n: { x: 0, y: 1 } },
    E: { p: { x: s.x + s.w, y: s.y + s.h / 2 }, n: { x: 1, y: 0 } },
    W: { p: { x: s.x, y: s.y + s.h / 2 }, n: { x: -1, y: 0 } },
  };
  const custom = PORT_ANCHORS[s.type]?.(s);
  const merged = custom ? { ...base, ...custom } : base;
  const rot = (((s.rotation || 0) % 360) + 360) % 360;
  if (!rot) return merged;
  const a = (rot * Math.PI) / 180;
  const cos = Math.cos(a);
  const sin = Math.sin(a);
  const cx = s.x + s.w / 2;
  const cy = s.y + s.h / 2;
  const out = {} as Record<PortKey, PortAnchor>;
  PORT_ORDER.forEach((k) => {
    const { p, n } = merged[k];
    const dx = p.x - cx;
    const dy = p.y - cy;
    out[k] = {
      p: { x: cx + dx * cos - dy * sin, y: cy + dx * sin + dy * cos },
      n: { x: n.x * cos - n.y * sin, y: n.x * sin + n.y * cos },
    };
  });
  return out;
}

/** The four cardinal connection ports of a shape (world coords). */
export function getPorts(s: Shape): Record<PortKey, Point> {
  const a = getPortAnchors(s);
  return { N: a.N.p, S: a.S.p, E: a.E.p, W: a.W.p };
}

/** The eight resize handles (corners + edge midpoints) of a shape. */
export function getHandles(s: Shape): Handle[] {
  const mx = s.x + s.w / 2;
  const my = s.y + s.h / 2;
  return [
    { x: s.x, y: s.y, id: 'nw' },
    { x: mx, y: s.y, id: 'n' },
    { x: s.x + s.w, y: s.y, id: 'ne' },
    { x: s.x + s.w, y: my, id: 'e' },
    { x: s.x + s.w, y: s.y + s.h, id: 'se' },
    { x: mx, y: s.y + s.h, id: 's' },
    { x: s.x, y: s.y + s.h, id: 'sw' },
    { x: s.x, y: my, id: 'w' },
  ];
}

/** Shortest distance from point `p` to the segment a→b. */
export function segDist(p: Point, a: Point, b: Point): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  if (!dx && !dy) return Math.hypot(p.x - a.x, p.y - a.y);
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / (dx * dx + dy * dy)));
  return Math.hypot(p.x - a.x - t * dx, p.y - a.y - t * dy);
}

/**
 * Nearest port key on shape `s` to world point (wx, wy).
 * Ties resolve in `PORT_ORDER`, so a shape that aliases several cardinals onto
 * one real face (an elbow) reports the natural key for that face.
 */
export function nearPort(s: Shape, wx: number, wy: number): PortKey {
  const ports = getPorts(s);
  let best: PortKey = 'E';
  let bestD = Infinity;
  PORT_ORDER.forEach((k) => {
    const p = ports[k];
    const d = Math.hypot(p.x - wx, p.y - wy);
    if (d < bestD) {
      bestD = d;
      best = k;
    }
  });
  return best;
}
